import { downloadBlobFromResponse, getCsrfValue } from "../../../api/ApiHelper";
import { DateFormatType, formatNullableDate } from "../../../utils/DateFormatter";
import * as WeightUtils from "../../../utils/WeightUtils";
import { Permission, userHasAnyPermission } from "../../../utils/permissions";
import instance from "../../../utils/i18n";
import { toMapFn } from "../../../utils/reducers";
import { getAliases } from "../../../common/components/Product/ProductAliases";
import { type DocumentListRow } from "../useOrderListState";
import { type OrderData, type OrderLineItemData } from "../../common/modelsV2";
import { type ReportType } from "../../../Cart/components/CartPage/Buttons/DownloadReportButton/api/downloadCartReport";
import { resolveApiUrl } from "../../../utils/url/urlResolver";
import { getOrderLineItemTotalPrice } from "~/OrdersQuotes/common/utils/OrderLineItemUtils";
import { text } from "../../../utils/PriceUtils";

export const downloadOrderHistoryReport = (data: DocumentListRow<OrderData>[], fileType: ReportType): Promise<void> => {
  const url = resolveApiUrl(`/my-account/report/${fileType}`);
  return fetch(url, {
    body: JSON.stringify(transformData(data)),
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    method: "POST",
    headers: {
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
  }).then(downloadBlobFromResponse);
};

interface OrderReportDownloadDTO {
  labels: Dict<string>;
  lines: OrderReportDownloadLineDTO[];
}

interface OrderReportDownloadLineDTO {
  salesOrderNumber: string;
  purchaseOrderNumber: string;
  datePlaced: string | undefined;
  totalNetPrice: number | undefined;
  currency: string | undefined;
  orderStatus: string;
  orderType: string;
  sublines: OrderReportDownloadSublineDTO[];
  account?: {
    code: string;
    salesOrg: string;
    endCustomerCodes: string[]; // Just one! It's a list because BE can't design make a proper API
  };
}

interface OrderReportDownloadSublineDTO {
  lineItem: string;
  sublineItem: string;
  product: string;
  description: string;
  materialNumber: string;
  customerMaterialNumber: string;
  packagingCode: string;
  quantity: number;
  confirmedQuantity: number;
  shipment: string;
  estimatedDeliveryDate: string;
  requestedDeliveryDate: string;
  weight: number | undefined;
  weightUnitOfMeasurement: string;
  weightUnit: string;
  netPrice: number | undefined;
  priceUnit: string;
  totalNetPrice: string | undefined;
  currency: string;
  lineItemStatus: string;
  priceReference: string;
  shipmentReferenceNumber: string;
  carrierName: string;
  invoiceNumber: string;
  invoiceDate: string;
  quoteNumber: string;
  comment: string;
  customerOrderNumber: string;
  marketDesignation: string;
}

const transformData = (data: DocumentListRow<OrderData>[]): OrderReportDownloadDTO => {
  const lines: OrderReportDownloadLineDTO[] = !data ? [] : data.map(transformLine);
  const reportLabels = toMapFn(
    getReportLabels(),
    (key) => key,
    (key) => instance.t(key),
  );
  return {
    labels: reportLabels,
    lines: lines,
  };
};

const transformLine = (row: DocumentListRow<OrderData>): OrderReportDownloadLineDTO => {
  const sublines: OrderReportDownloadSublineDTO[] = [];

  row.data.products.forEach((product) => {
    sublines.push(transformSubline(product));
    if (product.subEntries.length > 0) {
      product.subEntries.forEach((subEntry) => {
        const transformedSubEntry = transformSubline(subEntry);
        // copy packaging code from parent product
        transformedSubEntry.packagingCode = product.packCode;
        sublines.push(transformedSubEntry);
      });
    }
  });

  return {
    salesOrderNumber: row.data.soNumber,
    purchaseOrderNumber: row.data.poNumber,
    datePlaced: formatNullableDate(row.data.datePlaced, DateFormatType.DATE),
    totalNetPrice: !row.data.totalPrice ? undefined : row.data.totalPrice.value,
    currency: !row.data.totalPrice ? undefined : row.data.totalPrice.currencyIso,
    orderStatus: row.data.orderStatus,
    orderType: row.data.orderType,
    sublines: sublines,
    account: row.data.account
      ? {
          code: row.data.account.code,
          salesOrg: row.data.account.salesOrg,
          endCustomerCodes: [row.data.account.endCustomerCode],
        }
      : undefined,
  };
};

const transformSubline = (data: OrderLineItemData): OrderReportDownloadSublineDTO => {
  const totalPrice = getOrderLineItemTotalPrice(data);
  return {
    lineItem: data.lineNo ? data.lineNo.split(":")[0] : "",
    sublineItem: data.lineNo ? data.lineNo.split(":")[1] : "",
    product: data.product.designation,
    description: data.product.description || "",
    materialNumber: data.product.sapMaterialNumber,
    customerMaterialNumber: getAliases(data.product, "13-digits")
      .concat(getAliases(data.product, "15-digits", data.packCode))
      .join(", "),
    packagingCode: data.packCode,
    quantity: data.qty,
    confirmedQuantity: data.confirmedQty,
    shipment: data.location,
    estimatedDeliveryDate: formatNullableDate(data.deliveryDate, DateFormatType.DATE),
    requestedDeliveryDate: formatNullableDate(data.requestedDeliveryDate, DateFormatType.DATE),
    weight: data.weight ? data.weight.value : undefined,
    weightUnitOfMeasurement: data.weight ? WeightUtils.getPreferredUnitSymbol(data.weight) : "",
    weightUnit: instance.t("common.product.perPiece", { count: 1 }),
    netPrice: data.netPrice.value,
    priceUnit: instance.t("common.product.perPiece", { count: data.netPrice.unit }),
    totalNetPrice: text(totalPrice),
    currency: data.netPrice.currencyIso,
    lineItemStatus: data.status,
    priceReference: data.priceReference,
    shipmentReferenceNumber: data.invoiceData ? data.invoiceData.trackingNo : "",
    carrierName: data.invoiceData ? data.invoiceData.type : "",
    invoiceNumber: data.invoiceData ? data.invoiceData.invoiceNo : "",
    invoiceDate: data.invoiceData ? data.invoiceData.invoiceDate : "",
    quoteNumber: data.invoiceData ? data.quoteNumber : "",
    comment: userHasAnyPermission(Permission.ADD_VIEW_COMMENTS_DMA, Permission.ADD_VIEW_COMMENTS_PMA)
      ? data.comment
      : "",
    customerOrderNumber: data.customerOrderNumber,
    marketDesignation: data.product.marketDesignation,
  };
};

export const getReportLabels = (): string[] => {
  return [
    "orderListPage.table.header.soNumber",
    "orderListPage.table.header.poNumber",
    "page.abmonitor.tableHeader.csv.excel.lineItem",
    "page.abmonitor.tableHeader.csv.excel.subLineItem",
    "orderListPage.table.header.date",
    "order.report.header.total",
    "orderList.orderStatus",
    "orderDetailsPage.summary.orderType",
    "orderDetailsPage.table.header.product",
    "order.report.header.description",
    "order.report.header.matnr",
    "searchByInput.queryType.customMaterialNumber",
    "cartPage.table.header.packaging.title",
    "cartPage.table.header.quantity.title",
    "quoteListPage.table.header.confirmedQuantity",
    "quoteListPage.table.header.requestedQuantity",
    "cartPage.table.header.packaging.subtitle",
    "quoteListPage.table.header.estimatedDeliveryDate",
    "page.abmonitor.tableHeader.reqDeliveryDate",
    "order.report.header.weight",
    "order.report.weight.measurement",
    "orderDetailsPage.labels.weightUnit",
    "cartPage.table.header.price.title",
    "order.report.header.currency",
    "cartPage.table.header.price.subtitle",
    "orderDetailsPage.table.header.total",
    "orderDetailsPage.table.header.status",
    "cartPage.report.comment",
    "order.report.header.price",
    "order.report.header.shipment",
    "order.report.header.carrier",
    "order.report.header.invoice.no",
    "order.report.header.invoice.date",
    "order.report.header.quote",
    "cartPage.report.timestamp",
    "cartPage.report.customer.number",
    "order.report.header.customer.order.number",
    "quoteListPage.table.header.marketDesignation",
  ];
};
